import base64toBlob from '../../utils/mediaUtils/base64toBlob';
import loadImage from '../../utils/mediaUtils/loadImage';
import apiV2Service from '../api/api-v2.service';
import {EmbeddingSourceType, KnowledgeRagTopic} from './ai.enums';
import {KnowledgeRAGResponse, RemoteEmbeddingSource, RemoteKnowledgeRAGResponse} from './ai.types';

const BASE_URL = 'AI';

function createEmbedding(prompt: string) {
  return apiV2Service
    .executePostRequest<{data: number[]}>(`${BASE_URL}/Embeddings/Create`, {}, {prompt})
    .then((res) => res.data.data);
}

function createBatchEmbeddings(prompts: string[]) {
  return apiV2Service
    .executePostRequest<
      {prompt: string; embedding: {data: number[]}}[]
    >(`${BASE_URL}/Embeddings/CreateBatch`, {}, {prompts})
    .then((res) => res.data.map(({prompt, embedding}) => ({prompt, embedding: embedding.data})));
}

function regenerateCampaignFromPrompt(payload: {
  campaignId: number;
  campaignVersionId: number;
  prompt: string;
}) {
  return apiV2Service.executePostRequest(`${BASE_URL}/ReGenerateCampaignFromPrompt`, {}, payload);
}

function getEmbedding(sourceType: EmbeddingSourceType): Promise<RemoteEmbeddingSource[]> {
  return apiV2Service
    .executeGetRequest<RemoteEmbeddingSource[]>(`${BASE_URL}/Embeddings/${sourceType}`)
    .then((res) => res.data);
}

function outpaintImage(
  file: File,
  newWidth: number,
  newHeight: number,
  prompt: string,
): Promise<string[]> {
  const largestNewSize = Math.max(newWidth, newHeight);
  const size = largestNewSize < 256 ? 256 : largestNewSize < 512 ? 512 : 1024;

  return apiV2Service
    .uploadFile<{base64Images: string[]}>({
      endpoint: `${BASE_URL}/Images/Edit`,
      formDataKey: 'image',
      postData: {
        desiredWidth: size,
        desiredHeight: size,
        prompt: prompt || `Ad background image`,
        numberOfResults: '4',
      },
      file,
      params: {},
    })
    .then((res) => {
      return Promise.all(
        res.data.base64Images.map((b64) => {
          const blob = base64toBlob(b64);
          const url = URL.createObjectURL(blob);

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Failed to get canvas context');

          canvas.width = newWidth;
          canvas.height = newHeight;
          const largestSide = Math.max(newWidth, newHeight);
          const offsetX = (newWidth - largestSide) / 2;
          const offsetY = (newHeight - largestSide) / 2;

          return loadImage(url).then((img) => {
            ctx.drawImage(img, offsetX, offsetY, largestSide, largestSide);

            return canvas.toDataURL('image/png', 1);
          });
        }),
      );
    });
}

async function getNewOutpaintedImages({
  width,
  height,
  top,
  left,
  bottom,
  right,
  numberOfInstances,
}: {
  width: number;
  height: number;
  top: number;
  left: number;
  bottom: number;
  right: number;
  numberOfInstances: number;
}): Promise<string[]> {
  if (numberOfInstances < 1 || numberOfInstances > 4) {
    throw new Error('numberOfInstances must be between 1 and 4');
  }

  const totalWidth = width + left + right;
  const totalHeight = height + top + bottom;

  const backgroundColors = ['#FFE4E1', '#E0FFFF', '#F0FFF0', '#FFF0F5'];
  const shapeColors = ['#FF4500', '#4169E1', '#32CD32', '#FF69B4'];

  const results: string[] = [];

  for (let i = 0; i < numberOfInstances; i++) {
    const canvas = document.createElement('canvas');
    canvas.width = totalWidth;
    canvas.height = totalHeight;

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Fill background
    ctx.fillStyle = backgroundColors[i];
    ctx.fillRect(0, 0, totalWidth, totalHeight);

    // Draw shape in the center
    const centerX = totalWidth / 2;
    const centerY = totalHeight / 2;
    const shapeSize = Math.min(totalWidth, totalHeight) * 0.2;

    ctx.fillStyle = shapeColors[i];

    // Draw different shapes based on index
    switch (i) {
      case 0:
        // Circle
        ctx.beginPath();
        ctx.arc(centerX, centerY, shapeSize / 2, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 1:
        // Square
        ctx.fillRect(centerX - shapeSize / 2, centerY - shapeSize / 2, shapeSize, shapeSize);
        break;
      case 2:
        // Triangle
        ctx.beginPath();
        ctx.moveTo(centerX, centerY - shapeSize / 2);
        ctx.lineTo(centerX + shapeSize / 2, centerY + shapeSize / 2);
        ctx.lineTo(centerX - shapeSize / 2, centerY + shapeSize / 2);
        ctx.closePath();
        ctx.fill();
        break;
      case 3:
        // Diamond
        ctx.beginPath();
        ctx.moveTo(centerX, centerY - shapeSize / 2);
        ctx.lineTo(centerX + shapeSize / 2, centerY);
        ctx.lineTo(centerX, centerY + shapeSize / 2);
        ctx.lineTo(centerX - shapeSize / 2, centerY);
        ctx.closePath();
        ctx.fill();
        break;
    }

    results.push(canvas.toDataURL('image/png'));
  }

  return results;
}

async function askKnowledgeRAG({
  prompt,
  context,
  topics,
  includeFullAnswer = true,
}: {
  prompt: string;
  context: string;
  topics: KnowledgeRagTopic[];
  includeFullAnswer?: boolean;
}): Promise<KnowledgeRAGResponse> {
  const {data} = await apiV2Service.executePostRequest<RemoteKnowledgeRAGResponse>(
    `${BASE_URL}/KnowledgeRAG/Ask`,
    {},
    {
      prompt,
      context,
      topics,
      includeFullAnswer,
    },
  );

  return data;
}

const aiApi = {
  createEmbedding,
  regenerateCampaignFromPrompt,
  getEmbedding,
  outpaintImage,
  createBatchEmbeddings,
  getNewOutpaintedImages,
  askKnowledgeRAG,
};

export default aiApi;
